import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'

// AdminAudit - admin-only firehose of every mutation. Cursor pagination via
// the `before` query param. v1 shows the latest 50 rows; "Load older" appends
// the next 50 via setQueryData. No filtering UI yet - filter by entityType
// is a future-spec.

interface AuditEntry {
  id: number
  occurredAt: string
  actor: { id: number; name: string | null; avatarInitials: string | null } | null
  eventId: number | null
  entityType: string
  entityId: number | null
  action: string
  summary: string
}

async function fetchAudit(before: number | null): Promise<AuditEntry[]> {
  const url = before == null
    ? '/api/audit?limit=50'
    : `/api/audit?limit=50&before=${before}`
  const res = await fetch(url, { credentials: 'include' })
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(`${res.status} ${res.statusText}: ${text}`)
  }
  return res.json()
}

export function AdminAudit() {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['audit'],
    queryFn: () => fetchAudit(null),
    refetchInterval: 30_000
  })

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl mb-1">Audit log</h1>
          <p className="text-ink-soft">Every mutation across the system, newest first.</p>
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          className="px-3 py-2 rounded text-sm font-semibold border border-line text-ink-soft hover:text-green hover:border-green transition-colors"
        >
          Refresh
        </button>
      </div>

      {isLoading && <div className="text-ink-soft text-sm font-mono">Loading…</div>}

      {error && (
        <div className="bg-oxblood-soft border border-oxblood text-oxblood rounded p-4 text-sm">
          <div className="font-semibold mb-1">Could not load audit log</div>
          <pre className="text-xs whitespace-pre-wrap">{(error as Error).message}</pre>
        </div>
      )}

      {data && data.length === 0 && (
        <div className="bg-white border border-line rounded p-8 text-center text-sm text-ink-soft italic">
          No audit rows yet.
        </div>
      )}

      {data && data.length > 0 && (
        <div className="bg-white border border-line rounded overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wide text-ink-soft border-b border-line">
                <th className="px-3 py-2 font-semibold w-40">When</th>
                <th className="px-3 py-2 font-semibold w-40">Who</th>
                <th className="px-3 py-2 font-semibold w-28">Entity</th>
                <th className="px-3 py-2 font-semibold w-28">Action</th>
                <th className="px-3 py-2 font-semibold">Summary</th>
                <th className="px-3 py-2 font-semibold w-24">Event</th>
              </tr>
            </thead>
            <tbody>
              {data.map((row) => (
                <tr key={row.id} className="border-b border-line last:border-b-0 hover:bg-parchment transition-colors">
                  <td className="px-3 py-2 font-mono text-[11px] text-ink-soft whitespace-nowrap">
                    {new Date(row.occurredAt).toLocaleString('en-GB')}
                  </td>
                  <td className="px-3 py-2">
                    {row.actor ? (
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-green text-parchment text-[10px] font-semibold flex items-center justify-center">
                          {row.actor.avatarInitials ?? '?'}
                        </div>
                        <span className="text-xs">{row.actor.name}</span>
                      </div>
                    ) : (
                      <span className="text-xs italic text-ink-soft">System</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-ink-soft uppercase tracking-wide">
                    {row.entityType}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    <span className="font-semibold">{row.action}</span>
                  </td>
                  <td className="px-3 py-2">{row.summary}</td>
                  <td className="px-3 py-2 text-xs">
                    {row.eventId ? (
                      <Link to={`/events/${row.eventId}`} className="text-green hover:underline">
                        #{row.eventId}
                      </Link>
                    ) : (
                      <span className="italic text-ink-soft">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
